/**
 * S-75: MFA UI（challenge / enrollment）の inline style。
 *
 * 各 app の Tailwind / CSS 変数（hsl(var(--...))）の有無に依存せず一貫描画するため、
 * 共有コンポーネントでは具体色の inline style を用いる。
 */
export const mfaCardWrap = {
    display: 'flex',
    minHeight: '100vh',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '3rem 1rem',
    background: '#f4f4f6',
};
export const mfaCard = {
    width: '100%',
    maxWidth: 400,
    border: '1px solid #e2e2e8',
    borderRadius: 8,
    background: '#ffffff',
    padding: '1.5rem',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
};
export const mfaTitle = {
    textAlign: 'center',
    fontSize: '1.125rem',
    fontWeight: 700,
    color: '#1f2330',
    margin: 0,
};
export const mfaSubtitle = {
    marginTop: '0.5rem',
    textAlign: 'center',
    fontSize: '0.8125rem',
    color: '#6b7280',
    lineHeight: 1.6,
};
export const mfaForm = {
    marginTop: '1.25rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.875rem',
};
export const mfaLabel = {
    display: 'block',
    fontSize: '0.75rem',
    fontWeight: 600,
    color: '#1f2330',
};
export const mfaInput = {
    marginTop: '0.25rem',
    width: '100%',
    boxSizing: 'border-box',
    border: '1px solid #cdd0d8',
    borderRadius: 6,
    padding: '0.5rem 0.75rem',
    fontSize: '1.125rem',
    letterSpacing: '0.25em',
    textAlign: 'center',
};
export const mfaError = {
    margin: 0,
    background: '#fef2f2',
    borderRadius: 6,
    padding: '0.5rem 0.75rem',
    fontSize: '0.75rem',
    color: '#b91c1c',
};
export const mfaButton = (disabled) => ({
    display: 'inline-flex',
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    // S-89: tokens.css --primary（sky-600）と同系に統一（サインイン/MFA 画面の青を揃える）
    background: disabled ? '#9aa0ac' : '#0284c7',
    color: '#ffffff',
    fontSize: '0.875rem',
    fontWeight: 600,
    padding: '0.5rem 1rem',
    borderRadius: 6,
    border: 'none',
    cursor: disabled ? 'not-allowed' : 'pointer',
});
export const mfaSecondaryButton = {
    display: 'inline-flex',
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'transparent',
    color: '#6b7280',
    fontSize: '0.8125rem',
    fontWeight: 500,
    padding: '0.4rem 1rem',
    borderRadius: 6,
    border: 'none',
    cursor: 'pointer',
};
export const mfaSecretBox = {
    marginTop: '0.5rem',
    padding: '0.5rem 0.75rem',
    background: '#f4f4f6',
    border: '1px solid #e2e2e8',
    borderRadius: 6,
    fontSize: '0.8125rem',
    fontFamily: 'monospace',
    wordBreak: 'break-all',
    color: '#1f2330',
    textAlign: 'center',
};
export const mfaQrWrap = {
    display: 'flex',
    justifyContent: 'center',
    padding: '1rem',
    background: '#ffffff',
};
// ---- S-136: v2（docs/UI-GUIDE.md）。tokens.css の CSS 変数を参照する（app が tokens.css + ui-v2.css を
// 取り込んでいる前提 / SignInCard の signInV2 と同じ考え方）。白カード 30/26・枠線なし・影なし・文字 7 段。
const v2Ink = 'hsl(var(--ink))';
const v2Ink2 = 'hsl(var(--ink-2))';
const v2Ink3 = 'hsl(var(--ink-3))';
export const mfaV1 = {
    card: mfaCard,
    title: mfaTitle,
    subtitle: mfaSubtitle,
    form: mfaForm,
    label: mfaLabel,
    input: mfaInput,
    error: mfaError,
    button: mfaButton,
    secondaryButton: mfaSecondaryButton,
    secretBox: mfaSecretBox,
    qrWrap: mfaQrWrap,
    onColor: '#15803d',
    offColor: '#6b7280',
};
export const mfaV2 = {
    card: { width: '100%', borderRadius: 12, background: '#ffffff', padding: '26px 30px' },
    title: { fontSize: 18, fontWeight: 600, color: v2Ink, margin: 0 },
    subtitle: { marginTop: '0.5rem', fontSize: 15, color: v2Ink2, lineHeight: 1.7 },
    form: { marginTop: '1.25rem', display: 'flex', flexDirection: 'column', gap: '1rem' },
    label: { display: 'block', fontSize: 14, fontWeight: 500, color: v2Ink2 },
    input: {
        marginTop: '0.375rem',
        width: '100%',
        boxSizing: 'border-box',
        border: 'none',
        borderRadius: 8,
        background: 'hsl(var(--surface-2))',
        padding: '8px 14px',
        fontSize: 18,
        letterSpacing: '0.25em',
        textAlign: 'center',
        color: v2Ink,
        outlineColor: 'hsl(var(--brand))',
    },
    error: {
        margin: 0,
        background: 'hsl(var(--surface-2))',
        borderRadius: 8,
        padding: '12px 18px',
        fontSize: 14,
        color: 'hsl(var(--danger-ink))',
    },
    button: (disabled) => ({
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'hsl(var(--brand))',
        opacity: disabled ? 0.5 : 1,
        color: '#ffffff',
        fontSize: 15,
        fontWeight: 500,
        padding: '8px 20px',
        borderRadius: 8,
        border: 'none',
        cursor: disabled ? 'not-allowed' : 'pointer',
    }),
    secondaryButton: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'hsl(var(--surface-2))',
        color: 'hsl(var(--danger-ink))',
        fontSize: 15,
        fontWeight: 500,
        padding: '8px 16px',
        borderRadius: 8,
        border: 'none',
        cursor: 'pointer',
    },
    // 手動入力キーは等幅にせず Noto Sans のまま（S-135 で <code> の等幅を統一した判断に合わせる）。字間で読みやすくする
    secretBox: {
        marginTop: '0.5rem',
        padding: '12px 18px',
        background: 'hsl(var(--surface-2))',
        borderRadius: 8,
        fontSize: 15,
        letterSpacing: '0.12em',
        wordBreak: 'break-all',
        color: v2Ink,
        textAlign: 'center',
    },
    qrWrap: { display: 'flex', justifyContent: 'center', padding: '1rem', background: '#ffffff' },
    onColor: 'hsl(var(--success-ink))',
    offColor: v2Ink3,
};
//# sourceMappingURL=mfaStyles.js.map